import React, { useState, useEffect, useReducer, useRef, useMemo } from 'react';
import "react-grid-layout/css/styles.css"
import "react-resizable/css/styles.css"
import { Layout } from 'react-grid-layout';
import RGL, { WidthProvider } from 'react-grid-layout';
import ResponsiveCard from './ResponsiveCard';
import ResponsivePlot from './ResponsivePlot';
import Alert from '@mui/material/Alert';
import { Box } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { multiply, sortedLastIndexOf } from 'lodash';
import LowResImageViewer from '../appSamples/LowResImageViewer';
import axios from 'axios';
import SampleImageViewer from '../appSamples/SampleImageViewer';

const GridLayout = WidthProvider(RGL);

interface PlotGridProps {
  multiPlotData: any[];
  currentPage: number;
  unstagedData: any;
  isHorizontalLayout: boolean,
  setIsHorizontalLayout: React.Dispatch<React.SetStateAction<boolean>>;
  handleDelete: (key: number, type: string) => void;
  handleContentChange: (id: number, content: any) => void;
  layers?: any;
  layout?: any;
  isLoading?: boolean;
  loadingMessage?: string;
  loadingProgress?: number;
  isError?: boolean;
  errorMessage?: string;
  alerts?: any[]
  numColumns?: number;
  tileModeOn: boolean;
  onAddColumn?: () => void;
  onRemoveColumn?: () => void;
  tileSharedY: boolean;
  tileSharedX: boolean;
  setPlotConfigs: (config: { [key: string]: PlotConfig }, save: boolean) => void;
  plotConfig: PlotConfig;
  selectedItems: SelectedItems;
  setItems: (newSelectedItems: SelectedItems) => void;
  setTileSharedY: (tileSharedY: boolean) => void;
  setTileSharedX: (tileSharedX: boolean) => void;
  selectedMultiPlot?: number | null;
  setSelectedMultiPlot?: (id:number)=>void;
  openPlotOptionsModal: () => void;
  categoricalColumns: any;
  numericColumns: any;
  updateFilters: () => void;
  axisFiltersState: FilterState;
  setAxisFiltersState: React.Dispatch<React.SetStateAction<FilterState>>;
  setConfigureTab: () => void;
  analyses: Analysis[];
  imageViewerOpen: boolean;
  setImageViewerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDataPipeline: (id: number) => void;
  setPlotRendering: (isRendering: boolean) => void;
}

const PlotGrid: React.FC<PlotGridProps> = ({
  multiPlotData,
  currentPage,
  unstagedData,
  isHorizontalLayout,
  setIsHorizontalLayout,
  handleDelete,
  handleContentChange,
  layers: defaultLayers,
  layout: defaultLayout,
  isLoading: defaultIsLoading,
  loadingMessage = 'Loading...',
  loadingProgress,
  isError: defaultIsError,
  errorMessage: defaultErrorMessage,
  alerts: defaultAlerts,
  numColumns,
  tileModeOn,
  onAddColumn,
  onRemoveColumn,
  tileSharedY,
  setTileSharedY,
  tileSharedX,
  setTileSharedX,
  setPlotConfigs,
  plotConfig,
  selectedItems,
  setItems,
  selectedMultiPlot,
  setSelectedMultiPlot,
  openPlotOptionsModal,
  categoricalColumns,
  numericColumns,
  updateFilters,
  axisFiltersState,
  setAxisFiltersState,
  setConfigureTab,
  analyses,
  imageViewerOpen,
  setImageViewerOpen,
  setDataPipeline,
  setPlotRendering
}) => {

  const navbarHeight = 56;
  const plotGridToolBarHeight = 48;
  const [windowHeight, setWindowHeight] = useState(window.innerHeight- navbarHeight - plotGridToolBarHeight);

  const gridRowHeight = 100; // Controls how granular the content can be resized
  const theme = useTheme();

  const [selectedChannel, setSelectedChannel] = useState<number | undefined>(undefined);
  const [selectedTimepoint, setSelectedTimepoint] = useState<string | undefined>(undefined);

  // const [layout, setLayout] = useState<Layout[]>([]);
    
  const prevSelectedChannelRef = useRef<number>(-1)

  useEffect(() => {
    if (selectedChannel !== undefined && selectedChannel !== prevSelectedChannelRef.current) {
      if (!imageViewerOpen) {
        setImageViewerOpen(true);
      }

      prevSelectedChannelRef.current = selectedChannel;
    }
  }, [selectedChannel]);

  useEffect(() => {
    if (imageViewerOpen){
      setIsHorizontalLayout(false);
    }
    else {
      setIsHorizontalLayout(true);
    }
  }, [imageViewerOpen])

  useEffect(() => {
    
    const handleResize = () => {
      setWindowHeight(window.innerHeight - navbarHeight - plotGridToolBarHeight);
    };

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  const getGridLayout = (index: number): Layout => {
    const plotCount = multiPlotData.length;
    let w, h, x, y;

    const maxH = Math.floor(windowHeight/gridRowHeight) - 1;
    
    if (plotCount === 1) {
      // Single plot takes up the entire grid
      w = 4; h = maxH; x = 0; y = 0;
    } else if (plotCount === 2 && isHorizontalLayout) {
      // Two plots side by side
      w = 2; h = maxH; x = index * 2; y = 0;
    } else if (plotCount === 2 && !isHorizontalLayout) {
      // Two plots stacked vertically
      w = 4; h = maxH/2; x = 0; y = index & 2;
    } else if (plotCount === 3 || plotCount === 4) {
      // 2x2 grid
      w = 2; h = maxH/2; x = (index % 2) * 2; y = Math.floor(index / 2);
    } else {
      w = 1; h = 1; x = index % 4; y = Math.floor(index / 4);
    }

    return { x, y, w, h, i: index.toString() };
  };

  // useEffect(()=> {
  //   const layout: Layout[] = multiPlotData.map((val: any, index: number) => {
  //     return getGridLayout(index)
  //   })
  //   console.log("here")
    // setLayout(layout);
    // setLayout(prev => {
    //   const newPrev = prev.filter((val) => layout.map(l=>l.i).includes(val.i));
    //   return [...newPrev, ...layout]
    // })
  // }, [multiPlotData.length, isHorizontalLayout, windowHeight])

  const handleDownloadData = (plotlyData: any, plotConfig: PlotConfig) => {
    console.log("handleDownloadData");
    console.log("plotLayers: ", plotlyData);
    console.log("plotConfig: ", plotConfig);

    const rows: Record<string, string | number>[] = [];
    const yKey = plotConfig.ytitle !== '' && plotConfig.ytitle !== undefined ? plotConfig.ytitle : 'y';
    const xKey = plotConfig.xtitle !== '' && plotConfig.xtitle !== undefined ? plotConfig.xtitle : 'x';
    

    plotlyData.forEach((trace: any) => {
      const { x, y, z, hovertext, name, type } = trace;

      // Handle heatmap trace type
      if (type === 'heatmap') {
        // Prepare the headers (columns) to be the x values
        // const headers = [yKey, ...x];

        // Iterate over each y value (rows)
        y.forEach((yValue: any, rowIndex: number) => {
          const row: Record<string, string | number> = {
            [yKey]: yValue,
          };

          // Iterate over each x value (columns)
          x.forEach((xValue: any, colIndex: number) => {
            const zValue = z[rowIndex][colIndex]; // Corresponding z value for the heatmap
            row[xValue] = zValue;
          });

          rows.push(row);
        });

      } else {
        // Handle non-heatmap trace types
        hovertext.forEach((text: any, idx: any) => {
          const row: Record<string, string | number> = {
            Group: name,
            [xKey]: x[idx],
            [yKey]: y[idx],
          };

          text.split('<br />').forEach((pair: any) => {
            const [label, ...rest] = pair.split(':');
            const value = rest.join(':').trim(); // In case the value itself contains ':'
            if (label && value && label !== 'Value' && label !== 'Sample Group') {
              row[label.trim()] = value;
            }
          });

          rows.push(row);
        });
      }
    });

    if (rows.length === 0) return;

    // Get all unique headers, prioritizing x values for heatmap traces
    const headers = Array.from(new Set(rows.flatMap(row => Object.keys(row))));

    // Build CSV content
    const csvContent = [
      headers.join(','), // header row
      ...rows.map(row =>
        headers.map(h => JSON.stringify(row[h] ?? '')).join(',')
      )
    ].join('\n');

    // Construct the name of the CSV file to export
    const rawName = plotConfig.title || 'unnamed';
    const safeName = rawName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')   // Replace non-alphanumeric with underscores
      .replace(/^_+|_+$/g, '')       // Trim leading/trailing underscores
      .replace(/_+/g, '_');          // Collapse multiple underscores
    const name = `xsphera-cloud-${safeName}.csv`;
    

    // Trigger file download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', name);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log("Download Complete");
  
  };

  const renderCards = () => {
    if (!multiPlotData.some((plotData) => plotData?.isImage === true) && imageViewerOpen){
      multiPlotData.push({isImage: true});
    }
    else if(!imageViewerOpen){
      multiPlotData = multiPlotData.filter((plotData) => plotData?.isImage !== true);
    }

    return multiPlotData.map((plotData, index) => {
      const gridLayout = getGridLayout(index);
      const plotAlerts = plotData.plotAlerts ?? [];

      if (plotData?.isImage === true){
        return (
          <div key={index} data-grid={gridLayout}>
            <div className="drag-handle" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '20px', cursor: 'move' }}></div>
            <ResponsiveCard
              cardType='image'
              id={index}
            >
              {({ width, height }: { width: number; height: number }) => (
                <SampleImageViewer 
                  analyses={analyses} 
                  selectedChannel={selectedChannel} 
                  selectedTimepoint={selectedTimepoint} 
                  width={width} 
                  height={height}
                  setSelectedChannel={setSelectedChannel}
                  setSelectedTimepoint={setSelectedTimepoint}
                />
              )}
            </ResponsiveCard>
          </div>
        )
      }

      
      // console.log("plot", plotData?.plotId, plotData?.plotLayout?.title?.text, plotData)
      // console.log("cat data ", categoricalColumns)

      const alertBox = (
        <div style={{position:'absolute', top:'0px', left:'0px', width:'100%', zIndex:99, opacity:'80%'}}>
          {plotAlerts.length > 0 && plotAlerts.map((alert: any, alertIndex: number) => {
              setTimeout(() => alert.remove(), alert.duration * 1000);
              return (<Alert severity={alert.type ?? 'info'} onClose={() => alert.remove()} key={`${alert.message}-${alertIndex}`}>
                {alert.message ?? 'Unknown alert'}
              </Alert>)
            }
          )}
        </div>
      );

      // turn this on to enable top toolbar 
      const editMode = true;

      return (
        <div key={index} data-grid={gridLayout} >
          {alertBox}
          <ResponsiveCard
            isLoading={!defaultIsError && defaultIsLoading}
            loadingProgress={loadingProgress}
            message={loadingMessage}
            cardType={'plot'}
            id={index}
            onDelete={handleDelete}
            selected={plotData?.plotId===selectedMultiPlot}
            onClick={() => setSelectedMultiPlot !== undefined ? setSelectedMultiPlot(plotData?.plotId) : undefined}
            onEdit={(content) => handleContentChange(index, content)}
            filter={false}
            alerts={alertBox}
            edit={editMode}
            tileModeOn={tileModeOn}
            onAddColumn={onAddColumn}
            onRemoveColumn={onRemoveColumn}
            tileSharedY={tileSharedY}
            setTileSharedY={setTileSharedY}
            tileSharedX={tileSharedX}
            setTileSharedX={setTileSharedX}
            numColumns={numColumns}
            setPlotConfigs={setPlotConfigs}
            plotConfig={plotConfig}
            selectedItems={selectedItems}
            setItems={setItems}
            openPlotOptionsModal={openPlotOptionsModal}
            setSelectedMultiPlot={() => setSelectedMultiPlot !== undefined ? setSelectedMultiPlot(plotData?.plotId): undefined}
            categoricalColumns={categoricalColumns}
            numericColumns={numericColumns}
            updateFilters={updateFilters}
            axisFiltersState={axisFiltersState}
            setAxisFiltersState={setAxisFiltersState}
            setConfigureTab={setConfigureTab}
            handleDownloadData={() => handleDownloadData(plotData.plotLayers ?? defaultLayers ?? [], plotConfig)}
            setDataPipeline={setDataPipeline}
          >
            {({ width, height }: { width: number; height: number }) => (
              <ResponsivePlot
                id={index}
                width={width}
                height={editMode ? height - 70 : height}
                layout={plotData.plotLayout ?? defaultLayout ?? []}
                layers={plotData.plotLayers ?? defaultLayers ?? []}
                onEdit={() => {}}
                isError={plotData.isError ?? defaultIsError ?? false}
                errorMessage={plotData.errorMessage ?? defaultErrorMessage ?? ''}
                theme={theme}
                selectedChannel={selectedChannel}
                selectedTimepoint={selectedTimepoint}
                setSelectedChannel={setSelectedChannel}
                setSelectedTimepoint={setSelectedTimepoint}
                setPlotRendering={setPlotRendering}
              />
            )}
          </ResponsiveCard>
        </div>
      );
    });
  };

  return (
    <GridLayout
      cols={4}
      rowHeight={gridRowHeight}
      layout={unstagedData[currentPage]?.layout || []}
      onLayoutChange={()=>{}}
      className="layout"
      draggableHandle=".drag-handle"
      isResizable={true}
      isDraggable={true}
    >
      {renderCards()}
    </GridLayout>
  );
};

export default PlotGrid;